import React, { useMemo, useState } from 'react';
import { 
  ChevronDown, 
  Code2, 
  FileText, 
  Quote, 
  Check, 
  Loader2, 
  ChevronRight,
  FileEdit,
  Search,
  FlaskConical,
  Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  WriterTextDiff,
  WriterTextDiffChunk,
  WriterTextDiffSection,
  WriterToolActivity,
  WriterTurnDiff,
} from '@/hooks/useWriterThread';
import { isRewriteOf, wordDiff } from '@/lib/writer/wordDiff';
import {
  WRITER_PHASE_LABEL,
  writerToolBucket,
  type WriterToolBucket,
} from '@/lib/writer/turnStatus';

interface Props {
  textDiff?: WriterTextDiff;
  diff?: WriterTurnDiff;
  activities?: WriterToolActivity[];
  streaming?: boolean;
  /** True while the bubble's status line already names the current phase,
   *  so the chip must not repeat it or spin a second time. */
  summaryHidden?: boolean;
  unresolvedCites?: string[];
  onOpenSource?: (anchorId: string) => void;
}

/**
 * Unified "Turn Receipt" that groups what the agent did (tools) and
 * what changed (diffs) into a single coherent, accessible card.
 *
 * Compact by default, showing a humanized summary of actions and
 * magnitude-based chips for changed sections.
 */
const WriterTurnReceipt: React.FC<Props> = ({
  textDiff,
  diff,
  activities = [],
  streaming = false,
  summaryHidden = false,
  unresolvedCites = [],
  onOpenSource,
}) => {
  const [openSectionAnchor, setOpenSectionAnchor] = useState<string | null>(null);
  const [toolsExpanded, setToolsExpanded] = useState(false);

  const hasTools = activities.length > 0;
  const hasTextDiff = textDiff && textDiff.sections.length > 0;
  const hasStructuralDiff = diff && !diff.is_empty;
  const hasUnresolved = unresolvedCites.length > 0;

  if (!hasTools && !hasTextDiff && !hasStructuralDiff && !hasUnresolved) {
    if (!streaming) {
      return (
        <div className="max-w-[92%] text-[11px] text-muted-foreground italic px-1 py-1">
          No document changes this turn.
        </div>
      );
    }
    return null;
  }

  return (
    <div 
      className="w-full border-t border-border/50 bg-background/45 overflow-hidden flex flex-col transition-all duration-200"
      role="region"
      aria-label="Turn summary"
    >
      {/* 1. Header Row: Actions & Diffs Summary */}
      <div className="flex flex-col items-stretch gap-1.5 px-3 py-2.5">
        {hasTools && (
          <ToolSummaryChip
            activities={activities}
            expanded={toolsExpanded}
            summaryHidden={summaryHidden}
            onToggle={() => setToolsExpanded(!toolsExpanded)}
          />
        )}
        
        {hasTextDiff && textDiff.sections.map((section) => (
          <ScopeChip
            key={section.anchor_id}
            section={section}
            active={section.anchor_id === openSectionAnchor}
            onToggle={() =>
              setOpenSectionAnchor((prev) => (prev === section.anchor_id ? null : section.anchor_id))
            }
          />
        ))}

        {!hasTextDiff && hasStructuralDiff && (
          <StructuralDiffChips diff={diff} />
        )}

        {hasUnresolved && (
          <div className="flex items-center gap-1 rounded-full border border-destructive/30 bg-destructive/5 px-2 py-1 text-[10px] font-medium text-destructive">
            <span className="h-1 w-1 rounded-full bg-destructive animate-pulse" />
            {unresolvedCites.length} missing {unresolvedCites.length === 1 ? 'cite' : 'cites'}
          </div>
        )}
      </div>

      {/* 2. Expanded Content Areas */}
      {toolsExpanded && hasTools && (
        <div className="border-t border-border/60 bg-muted/20 px-3 py-2 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
          <ToolActivityList activities={activities} />
        </div>
      )}

      {openSectionAnchor && hasTextDiff && (
        <div className="border-t border-border/60 px-3.5 py-3 diff-scan-once animate-in fade-in duration-300" key={openSectionAnchor}>
          {textDiff.sections
            .filter((s) => s.anchor_id === openSectionAnchor)
            .map((s) => (
              <SectionBody key={s.anchor_id} section={s} onOpenSource={onOpenSource} />
            ))}
        </div>
      )}

      {textDiff?.truncated && (
        <p className="px-3.5 pb-2 text-[10px] text-muted-foreground border-t border-border/40 pt-1">
          Large turn — some changes are summarised. Open the source for the full picture.
        </p>
      )}
    </div>
  );
};

/** Compact chip for tool activity summary */
const ToolSummaryChip: React.FC<{
  activities: WriterToolActivity[];
  expanded: boolean;
  summaryHidden: boolean;
  onToggle: () => void;
}> = ({ activities, expanded, summaryHidden, onToggle }) => {
  const running = activities.some((a) => a.status === 'running');
  const failed = activities.some((a) => a.ok === false);
  
  const phases = humanizedPhases(activities);
  const summary = phases.join(' · ');

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className={cn(
        'group flex min-h-7 w-full items-center gap-2 rounded-lg border px-2.5 py-1 text-left text-[11px] transition-all duration-200',
        expanded
          ? 'border-primary/50 bg-primary/10 text-foreground shadow-sm'
          : 'border-border/70 bg-background/60 text-muted-foreground hover:text-foreground hover:border-border',
      )}
    >
      {!summaryHidden && (
        <span className="flex h-3.5 w-3.5 items-center justify-center">
          {running ? (
            <Loader2 className="h-3 w-3 animate-spin text-primary" aria-hidden />
          ) : failed ? (
            <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
          ) : (
            <Check className="h-3 w-3 text-primary" aria-hidden />
          )}
        </span>
      )}
      <span className="font-medium text-foreground/90 text-[11px]">Work details</span>
      {!summaryHidden && (
        <span className="min-w-0 flex-1 truncate text-muted-foreground/80">{summary}</span>
      )}
      {expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
    </button>
  );
};

const CHANGE_VERB: Record<WriterTextDiffSection['change'], string> = {
  created: 'New',
  edited: 'Edited',
  removed: 'Removed',
};

const ScopeChip: React.FC<{
  section: WriterTextDiffSection;
  active: boolean;
  onToggle: () => void;
}> = ({ section, active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={active}
    className={cn(
      'group flex min-h-7 w-full items-center gap-2 rounded-lg border px-2.5 py-1 text-left text-[11px] transition-all duration-200',
      active
        ? 'border-primary/50 bg-primary/10 text-foreground shadow-sm'
        : 'border-border/70 bg-background/60 text-muted-foreground hover:text-foreground hover:border-border',
    )}
  >
    {section.kind === 'bib' ? (
      <Quote className="h-3 w-3 shrink-0" />
    ) : (
      <FileText className="h-3 w-3 shrink-0" />
    )}
    <span className="font-medium text-foreground/90 truncate max-w-[14rem]">{section.title}</span>
    <span className="ml-auto text-muted-foreground/80 hidden sm:inline">
      {CHANGE_VERB[section.change]} · {section.magnitude}
    </span>
    <MagnitudeBar weight={section.weight} />
    <ChevronDown
      className={cn('h-3 w-3 shrink-0 transition-transform duration-200', active && 'rotate-180')}
    />
  </button>
);

const MagnitudeBar: React.FC<{ weight: number }> = ({ weight }) => (
  <span className="flex items-end gap-[2px]" aria-hidden>
    {[1, 2, 3, 4].map((i) => (
      <span
        key={i}
        className={cn(
          'w-[3px] rounded-sm transition-colors',
          i === 1 && 'h-[4px]',
          i === 2 && 'h-[6px]',
          i === 3 && 'h-[8px]',
          i === 4 && 'h-[10px]',
          i <= weight ? 'bg-diff-add' : 'bg-border',
        )}
      />
    ))}
  </span>
);

const SectionBody: React.FC<{
  section: WriterTextDiffSection;
  onOpenSource?: (anchorId: string) => void;
}> = ({ section, onOpenSource }) => {
  const [showSource, setShowSource] = useState(false);
  const rows = useMemo(() => pairChunks(section.chunks), [section.chunks]);

  return (
    <div className="space-y-2">
      <div className="space-y-1.5 font-serif text-[13px] leading-relaxed text-foreground">
        {rows.map((row, i) => (
          <DiffRow key={i} row={row} />
        ))}
      </div>

      {section.cites_added.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Cited: {section.cites_added.join(', ')}
        </p>
      )}

      <div className="flex items-center gap-3 pt-0.5">
        <button
          type="button"
          onClick={() => setShowSource((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <Code2 className="h-3 w-3" />
          {showSource ? 'Hide LaTeX' : 'Show LaTeX'}
        </button>
        {onOpenSource && section.kind === 'section' && (
          <button
            type="button"
            onClick={() => onOpenSource(section.anchor_id)}
            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            Open in draft
          </button>
        )}
      </div>

      {showSource && (
        <pre className="max-h-64 overflow-auto rounded-md border border-border/60 bg-muted/40 p-2 text-[11px] font-mono leading-snug">
          {section.raw.map((line, i) => (
            <div
              key={i}
              className={cn(
                'whitespace-pre-wrap',
                line.op === 'insert' && 'bg-diff-add-surface text-diff-add',
                line.op === 'delete' && 'bg-diff-remove-surface text-diff-remove line-through',
                line.op === 'equal' && 'text-muted-foreground',
              )}
            >
              {line.op === 'insert' ? '+ ' : line.op === 'delete' ? '− ' : '  '}
              {line.text}
            </div>
          ))}
        </pre>
      )}
    </div>
  );
};

const StructuralDiffChips: React.FC<{ diff: WriterTurnDiff }> = ({ diff }) => {
  const chips: Array<{ label: string; tone: 'add' | 'edit' | 'remove' }> = [];
  for (const s of diff.sections_created) chips.push({ label: `+ ${s.label}`, tone: 'add' });
  for (const s of diff.sections_patched) chips.push({ label: `~ ${s.label}`, tone: 'edit' });
  for (const s of diff.sections_removed) chips.push({ label: `− ${s.label}`, tone: 'remove' });
  
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((c, i) => (
        <span 
          key={i} 
          className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-medium border",
            c.tone === 'add' && "bg-diff-add-surface text-diff-add border-diff-add/20",
            c.tone === 'edit' && "bg-primary/5 text-primary border-primary/20",
            c.tone === 'remove' && "bg-diff-remove-surface text-diff-remove border-diff-remove/20"
          )}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
};

const ToolActivityList: React.FC<{ activities: WriterToolActivity[] }> = ({ activities }) => {
  const grouped = new Map<WriterToolBucket, WriterToolActivity[]>();
  for (const act of activities) {
    const bucket = writerToolBucket(act.name);
    const list = grouped.get(bucket) ?? [];
    list.push(act);
    grouped.set(bucket, list);
  }

  return (
    <div className="space-y-3">
      {Array.from(grouped.entries()).map(([bucket, acts]) => {
        const meta = BUCKETS[bucket];
        const Icon = meta.icon;
        return (
          <div key={bucket} className="space-y-1">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <Icon className="h-3 w-3" />
              {meta.label}
            </div>
            <ul className="space-y-1 pl-4">
              {acts.map((act, i) => {
                const detail = detailFor(act);
                return (
                  <li key={i} className="flex items-center gap-2 text-xs text-foreground/80">
                    <span className="flex h-3 w-3 items-center justify-center">
                      {act.status === 'running' ? (
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      ) : act.ok === false ? (
                        <span className="h-1 w-1 rounded-full bg-destructive" />
                      ) : (
                        <Check className="h-2.5 w-2.5 text-primary/80" />
                      )}
                    </span>
                    <span className="text-foreground/90 font-medium">
                      {TOOL_LABEL[act.name] ?? act.name}
                    </span>
                    {detail && (
                      <span className="text-muted-foreground truncate italic">· {detail}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

// --- Helpers adapted from WriterChangePlanCard & WriterTurnDiffCard ---

interface BucketMeta { key: WriterToolBucket; label: string; icon: React.ComponentType<{ className?: string }>; }
const BUCKETS: Record<WriterToolBucket, BucketMeta> = {
  edits: { key: 'edits', label: 'Edits', icon: FileEdit },
  research: { key: 'research', label: 'Research', icon: Search },
  diagnostics: { key: 'diagnostics', label: 'Checks', icon: FlaskConical },
  other: { key: 'other', label: 'Other', icon: Sparkles },
};

const TOOL_LABEL: Record<string, string> = {
  patch_section: 'Patched section', replace_section: 'Replaced section',
  create_section: 'Created section', set_preamble: 'Updated preamble',
  add_to_bib: 'Added citation', materialize: 'Snapshotted draft',
  insert_figure: 'Inserted figure', format_tex: 'Formatted LaTeX',
  apply_template: 'Applied template', remember: 'Noted for later',
  search_literature: 'Searched literature', resolve_citation: 'Resolved citation',
  plot_from_data: 'Plotted data', generate_diagram: 'Generated diagram',
  table_from_data: 'Built table', image_to_tikz: 'Traced figure',
  paper_map: 'Read outline', list_sections: 'Listed sections',
  read_section: 'Read section', validate_document: 'Validated draft',
  calculate: 'Computed', derive: 'Derived', check_math: 'Checked math',
  check_claims: 'Checked claims',
};

function detailFor(act: WriterToolActivity): string | null {
  const a = (act.args ?? {}) as Record<string, unknown>;
  const pick = (k: string) => (typeof a[k] === 'string' ? (a[k] as string) : undefined);
  switch (act.name) {
    case 'patch_section': case 'replace_section': case 'create_section': case 'read_section':
      return pick('anchor') ?? pick('id') ?? pick('title') ?? null;
    case 'add_to_bib': return pick('title') ?? pick('doi') ?? null;
    case 'search_literature': case 'resolve_citation': {
      const q = pick('query'); return q ? `“${q.slice(0, 60)}”` : pick('doi') ?? null;
    }
    case 'insert_figure': case 'image_to_tikz': return pick('caption') ?? pick('anchor') ?? null;
    case 'remember': return act.notes?.[0]?.note ?? null;
    case 'materialize': return pick('title') ?? null;
    default: return null;
  }
}

type Row =
  | { kind: 'gap'; count: number }
  | { kind: 'equal'; text: string }
  | { kind: 'insert'; text: string }
  | { kind: 'delete'; text: string }
  | { kind: 'rewrite'; before: string; after: string };

export function pairChunks(chunks: WriterTextDiffChunk[]): Row[] {
  const rows: Row[] = [];
  for (let i = 0; i < chunks.length; i += 1) {
    const chunk = chunks[i];
    if (chunk.op === 'gap') { rows.push({ kind: 'gap', count: chunk.count }); continue; }
    const next = chunks[i + 1];
    if (chunk.op === 'delete' && next && next.op === 'insert' && isRewriteOf(chunk.text, next.text)) {
      rows.push({ kind: 'rewrite', before: chunk.text, after: next.text });
      i += 1; continue;
    }
    rows.push({ kind: chunk.op, text: chunk.text } as Row);
  }
  return rows;
}

const DiffRow: React.FC<{ row: Row }> = ({ row }) => {
  if (row.kind === 'gap') {
    return (
      <p className="text-[11px] font-sans text-muted-foreground/70 select-none">
        ⋯ {row.count} unchanged {row.count === 1 ? 'sentence' : 'sentences'}
      </p>
    );
  }
  if (row.kind === 'equal') return <p className="text-muted-foreground/80">{row.text}</p>;
  if (row.kind === 'insert') return (
    <p><span className="text-diff-add underline decoration-diff-add/60 decoration-1 underline-offset-[3px]">{row.text}</span></p>
  );
  if (row.kind === 'delete') return (
    <p><span className="text-diff-remove line-through decoration-diff-remove/60">{row.text}</span></p>
  );
  const tokens = wordDiff(row.before, row.after);
  return (
    <p>
      {tokens.map((token, i) =>
        token.op === 'equal' ? (
          <span key={i}>{token.text}</span>
        ) : token.op === 'insert' ? (
          <span key={i} className="text-diff-add underline decoration-diff-add/60 decoration-1 underline-offset-[3px]">{token.text}</span>
        ) : (
          <span key={i} className="text-diff-remove line-through decoration-diff-remove/60">{token.text}</span>
        ),
      )}
    </p>
  );
};

function humanizedPhases(activities: WriterToolActivity[]): string[] {
  const labels: string[] = [];
  const add = (label: string) => {
    if (!labels.includes(label)) labels.push(label);
  };
  for (const activity of activities) {
    add(WRITER_PHASE_LABEL[writerToolBucket(activity.name)]);
  }
  return labels;
}

export default WriterTurnReceipt;
